import React from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';

import useSharedStyles from './theme/pbdqShared.styles';
import { pbdqFont } from './theme/pbdqTokens';
import { sdsColors, sdsRadius, sdsBtnBase } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import PbdqGradientStripe from './components/PbdqGradientStripe.comp';
import PbdqCapabilityTabs from './components/PbdqCapabilityTabs.comp';

import CapabilitiesHero from '../../assets/images/PoweredByDQ/CapabilitiesHero.png';

const useStyles = makeStyles(() => ({
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220, 231, 243, 0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
    },
    heroInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(32px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    heroContent: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(38px, 4.6vw, 66px)',
        lineHeight: 1.02,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        fontFamily: pbdqFont,
        color: '#1B2430',
    },
    heroTitleHighlight: {
        display: 'block',
        color: '#ED6628',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
        fontFamily: pbdqFont,
    },
    heroArt: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '@media (max-width: 820px)': {
            minHeight: 0,
            maxWidth: 'none',
        },
    },
    heroPhotoImg: {
        width: '100%',
        height: 'auto',
        display: 'block',
    },

    tabsSection: {
        padding: '0 0 56px',
    },

    addonsSection: {
        background: '#F4F6F9',
    },
    addonsInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    addonsBox: {
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3vw, 44px)',
        borderRadius: 24,
        background: '#FBF1EA',
        boxSizing: 'border-box',
    },
    addonsHeader: {
        display: 'grid',
        gap: 12,
    },
    addonsTitle: {
        fontSize: 'clamp(28px, 3.4vw, 46px)',
        lineHeight: 1.06,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    addonsTitleHighlight: {
        color: '#ED6628',
    },
    addonsLead: {
        fontSize: 16,
        lineHeight: 1.55,
        margin: 0,
        maxWidth: '60ch',
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    addonsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
    },
    addonCard: {
        display: 'grid',
        gap: 14,
        alignContent: 'start',
        padding: 'clamp(22px, 2.6vw, 30px)',
        borderRadius: 20,
        background: '#FFFFFF',
        border: '1px solid #F0E2D6',
        boxSizing: 'border-box',
    },
    addonBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 48,
        height: 48,
        borderRadius: 12,
        flexShrink: 0,
    },
    addonTitle: {
        fontSize: 20,
        fontWeight: 800,
        letterSpacing: '-0.015em',
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    addonItem: {
        display: 'grid',
        gap: 0,
    },
    addonItemLabel: {
        fontSize: 15,
        fontWeight: 700,
        margin: '0 0 4px',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    addonItemDesc: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    addonBullets: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 8,
        fontSize: 14.5,
        lineHeight: 1.5,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    addonBullet: {
        display: 'grid',
        gridTemplateColumns: '12px 1fr',
        gap: 10,
        alignItems: 'baseline',
    },
    addonBulletDot: {
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: '#ED6628',
        display: 'inline-block',
    },

    standardsSection: {
        background: '#FFFFFF',
    },
    standardsInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    standardsRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(28px, 4vw, 64px)',
        alignItems: 'center',
    },
    donutSvg: {
        flex: '0 1 260px',
        width: 'min(260px, 70vw)',
        height: 'auto',
    },
    standardsContent: {
        flex: '1 1 400px',
        minWidth: 0,
        display: 'grid',
        gap: 14,
    },
    standardsTitle: {
        fontSize: 'clamp(28px, 3.4vw, 46px)',
        lineHeight: 1.06,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    standardsTitleHighlight: {
        color: '#ED6628',
    },
    standardsBody: {
        fontSize: 16,
        lineHeight: 1.65,
        margin: 0,
        color: '#5B6470',
        fontFamily: pbdqFont,
    },
    standardsPills: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 6,
    },
    standardsPill: {
        padding: '7px 12px',
        borderRadius: 999,
        background: '#FFFFFF',
        border: '1px solid #E6E9EE',
        fontSize: 13,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },

    ctaSection: {
        marginTop: 'clamp(24px, 3vw, 40px)',
        background: '#172939',
        color: '#FFFFFF',
    },
    ctaInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    ctaTitle: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '22ch',
        color: '#FFFFFF',
        fontFamily: pbdqFont,
    },
    ctaBtn: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 54,
        padding: '0 26px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        textDecoration: 'none',
        fontSize: 16,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        transition: 'background 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            textDecoration: 'none',
        },
    },
}));

const CAPABILITIES = [
    {
        number: '01',
        stage: 'Define',
        name: 'FrameworkCreator',
        subtitle: 'Define a shared framework for AI-digital capacity, mapped to IEEE 3527.1 and 100+ global standards.',
        body: 'Agree what AI-digital literacy means for your people — aligned to recognized global standards — so everyone is working toward one clear target. FrameworkCreator combines productized tooling with expert-guided methodology, which is what keeps the result rigorous, consistent, and globally interoperable.',
        role: 'Defines a shared vision on AI-digital competencies and systems.',
        usedIn: 'Workforce AI Readiness · LearnCreator (optional national framework alignment)',
        inPractice: 'Gyeongsangbuk-do Province used FrameworkCreator to build a first-of-its-kind AI-digital competency framework and tiered certification system.',
    },
    {
        number: '02',
        stage: 'Measure',
        name: 'AssessCreator',
        subtitle: 'Measure AI-digital capacity with standardized or custom assessments — across individuals, cohorts, and whole populations, tracked in real time.',
        body: 'Design, deploy, and analyze assessments with multi-level dashboards and real-time analytics at individual, group, organization, and sector level.',
        role: 'Measures a baseline and establishes an evidence layer for action',
        usedIn: 'COS Assessments, School COS Readiness, Workforce AI Readiness, Program Validation',
        inPractice: 'National University of Singapore (NUS) used AssessCreator to measure digital wellbeing of 4.000+ people across four different countries.',
    },
    {
        number: '03',
        stage: 'Build',
        name: 'LearnCreator',
        subtitle: 'Build custom programs that educate, train, and engage on AI-digital capacity — with impact measured throughout.',
        body: 'An AI-powered program creation engine that generates complete learning experiences with assessment embedded. It compresses program creation from months or years into days, at a fraction of conventional development cost — even for teams with no prior curriculum expertise.',
        role: 'Create learning programs with built-in assessments',
        usedIn: 'School COS Readiness · LearnCreator · Workforce AI Readiness',
        inPractice: 'The LEGO Group built digital citizenship programs using Powered by DQ and reached millions of children.',
    },
    {
        number: '04',
        stage: 'Validate',
        name: 'DQ Stars',
        subtitle: 'Validate frameworks, assessments, and programs against global standards, and earn recognition.',
        body: 'DQ Stars measures how well an existing framework, assessment, or piece of learning content aligns to the IEEE DQ standard and other 100+ established frameworks — so funders, regulators, and partners can trust it, and so quality programs can differentiate themselves and scale.',
        role: 'Validates any program against global standards.',
        usedIn: 'Available as an add-on to every solution',
        inPractice: 'Alannah & Madeline Foundation, Accenture, and Netsafe used DQ Stars to align their online safety program with global standards and frameworks.',
    },
    {
        number: '05',
        stage: 'Benchmark',
        name: 'DQ Index Connect',
        subtitle: 'Benchmark AI-digital capacity against the world, and see your standing in national and global context.',
        body: 'Turn your measurement data into comparative intelligence and policy insight by connecting to the world’s largest digital intelligence databank. Most often deployed alongside AssessCreator — benchmarking only means something when there is underlying measurement behind it.',
        role: 'Benchmarks against the national and global digital intelligence dataset.',
        usedIn: 'COS Assessments · available as an add-on to every solution',
        inPractice: 'The Global Cybersecurity Forum (GCF) used DQ Index Connect to compare each country’s child online safety data against global benchmarks.',
    },
];

const InfraIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m16 18 6-6-6-6 M8 6l-6 6 6 6" />
    </svg>
);
const ConfigIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 6h10 M18 6h2 M4 12h4 M12 12h8 M4 18h12 M20 18h0 M14 4v4 M8 10v4 M16 16v4" />
    </svg>
);
const ValueIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 2v20 M17 6.5c0-1.9-2.2-3-5-3s-5 1.1-5 3 2.2 2.5 5 3 5 1.6 5 3.5-2.2 3.5-5 3.5-5-1.6-5-3.5" />
    </svg>
);

const PbdqCapabilitiesPageContent = () => {
    const shared = useSharedStyles();
    const classes = useStyles();

    return (
        <div className={shared.page} style={{ background: '#FFFFFF' }}>
            <PbdqNav />
            {/* <PbdqGradientStripe sticky /> */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroInner}>
                    <div className={classes.heroContent}>
                        <h1 className={classes.heroTitle}>
                            <span>Five capabilities.</span>
                            <span className={classes.heroTitleHighlight}>One lifecycle.</span>
                        </h1>
                        <p className={classes.heroLead}>
                            Powered by DQ's five capability layers form its backbone, and each maps to one stage of the AI-digital literacy lifecycle: define what capacity means, measure it, build it, validate it, and benchmark it against the world.
                        </p>
                    </div>
                    <div className={classes.heroArt}>
                        <img
                            src={CapabilitiesHero}
                            alt="Five capabilities lifecycle: define, measure, build, validate, benchmark"
                            className={classes.heroPhotoImg}
                        />
                    </div>
                </div>
            </section>

            <section aria-label="The five stages" style={{ background: '#FFFFFF' }}>
                <PbdqCapabilityTabs />
            </section>

            <section aria-label="Add-ons" className={classes.addonsSection}>
                <div className={classes.addonsInner}>
                    <div className={classes.addonsBox}>
                        <div className={classes.addonsHeader}>
                            <h2 className={classes.addonsTitle}>
                                Extend it with <span className={classes.addonsTitleHighlight}>add-ons</span>
                            </h2>
                            <p className={classes.addonsLead}>
                                Add-ons attach to any deployment and are sold as line items rather than packages.
                            </p>
                        </div>
                        <div className={classes.addonsGrid}>
                            <div className={classes.addonCard}>
                                <span
                                    className={classes.addonBadge}
                                    style={{ background: '#E1F0F9', color: '#1F5F86' }}
                                >
                                    <InfraIcon />
                                </span>
                                <h3 className={classes.addonTitle}>Infrastructure</h3>
                                <div className={classes.addonItem}>
                                    <h4 className={classes.addonItemLabel}>API &amp; Platform Mode</h4>
                                    <p className={classes.addonItemDesc}>
                                        Embed PbDQ inside your own LMS or platform, with API access and single sign-on.
                                    </p>
                                </div>
                            </div>
                            <div className={classes.addonCard}>
                                <span
                                    className={classes.addonBadge}
                                    style={{ background: '#EDF2D5', color: '#4E6A12' }}
                                >
                                    <ConfigIcon />
                                </span>
                                <h3 className={classes.addonTitle}>Configuration</h3>
                                <div className={classes.addonItem}>
                                    <h4 className={classes.addonItemLabel}>Framework anchoring</h4>
                                    <p className={classes.addonItemDesc}>
                                        Anchor PbDQ to a framework you already use, such as a national ministry AI-literacy framework.
                                    </p>
                                </div>
                                <div className={classes.addonItem}>
                                    <h4 className={classes.addonItemLabel}>Localization and translation</h4>
                                    <p className={classes.addonItemDesc}>
                                        Deploy in 30+ languages with no additional setup for standardized content.
                                    </p>
                                </div>
                            </div>
                            <div className={classes.addonCard}>
                                <span
                                    className={classes.addonBadge}
                                    style={{ background: '#FAEDDA', color: '#8A5A10' }}
                                >
                                    <ValueIcon />
                                </span>
                                <h3 className={classes.addonTitle}>Value</h3>
                                <ul className={classes.addonBullets}>
                                    <li className={classes.addonBullet}>
                                        <span className={classes.addonBulletDot} />
                                        <span>Additional dashboards</span>
                                    </li>
                                    <li className={classes.addonBullet}>
                                        <span className={classes.addonBulletDot} />
                                        <span>Extra assessment-creation capacity</span>
                                    </li>
                                    <li className={classes.addonBullet}>
                                        <span className={classes.addonBulletDot} />
                                        <span>Multi-language support</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section aria-label="The standards backbone" className={classes.standardsSection}>
                <div className={classes.standardsInner}>
                    <div className={classes.standardsRow}>
                        <svg
                            viewBox="0 0 260 260"
                            className={classes.donutSvg}
                            role="img"
                            aria-label="IEEE 3527.1, the shared foundation"
                        >
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#68C3EE" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(-90 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#718DC4" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(-45 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#D95997" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(0 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#B9316D" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(45 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#EDB459" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(90 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#BCD253" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(135 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#EDE953" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(180 130 130)" />
                            <circle cx="130" cy="130" r="100" fill="none" stroke="#DD6E3B" strokeWidth="30" strokeDasharray="72.5398 555.7787" transform="rotate(225 130 130)" />
                            <text x="130" y="120" textAnchor="middle" fontFamily="Figtree, sans-serif" fontWeight="800" fontSize="28" fill="#1B2430">IEEE</text>
                            <text x="130" y="152" textAnchor="middle" fontFamily="Figtree, sans-serif" fontWeight="800" fontSize="28" fill="#1B2430">3527.1™</text>
                            <text x="130" y="174" textAnchor="middle" fontFamily="Figtree, sans-serif" fontSize="11" fill="#5B6470">the shared foundation</text>
                        </svg>
                        <div className={classes.standardsContent}>
                            <h2 className={classes.standardsTitle}>
                                The <span className={classes.standardsTitleHighlight}>standards</span> backbone
                            </h2>
                            <p className={classes.standardsBody}>
                                Every capability runs on the same foundation: the globally recognized standards for AI and digital literacy, paired with research-backed pedagogy. That is what lets a result from Powered by DQ stand up to any stakeholder, in any market.
                            </p>
                            <p className={classes.standardsBody}>
                                The backbone is maintained continuously, so what you build on stays current as standards and regulations change.
                            </p>
                            <div className={classes.standardsPills}>
                                <span className={classes.standardsPill}>IEEE 3527.1™</span>
                                <span className={classes.standardsPill}>UNESCO</span>
                                <span className={classes.standardsPill}>OECD</span>
                                <span className={classes.standardsPill}>EU DigComp</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section aria-label="Next step" className={classes.ctaSection}>
                <div className={classes.ctaInner}>
                    <h3 className={classes.ctaTitle}>Use one on its own. Or connect all five.</h3>
                    <Link to={pbdqRoutes.solutions} className={classes.ctaBtn}>
                        See the solutions built on them
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </Link>
                </div>
            </section>

            {/* <PbdqGradientStripe /> */}
            <PbdqFooter />
        </div>
    );
};

export default PbdqCapabilitiesPageContent;
